import { motion } from "framer-motion";
import { Phone, MessageCircle, Globe, Heart, Users } from "lucide-react";

const resources = [
  {
    icon: Phone,
    name: "988 Suicide & Crisis Lifeline",
    detail: "Call or text 988",
    href: "tel:988",
    description: "Free, confidential support available 24/7 for anyone in distress.",
  },
  {
    icon: MessageCircle,
    name: "Crisis Text Line",
    detail: "Text HOME to 741741",
    href: "sms:741741&body=HOME",
    description: "Free crisis counseling via text message, available 24/7.",
  },
  {
    icon: Globe,
    name: "National Domestic Violence Hotline",
    detail: "1-800-799-7233",
    href: "tel:18007997233",
    description: "Confidential support for those affected by domestic violence.",
  },
  {
    icon: Heart,
    name: "The Trevor Project",
    detail: "thetrevorproject.org/get-help",
    href: "https://www.thetrevorproject.org/get-help/",
    description: "The Trevor Project has trained counselors who understand the challenges LGBTQ+ young people face and are available for support 24/7.",
  },
  {
    icon: Users,
    name: "YouthLine",
    detail: "theyouthline.org",
    href: "https://www.theyouthline.org/",
    description: "YouthLine is a free peer-to-peer help, support, and crisis line for youth up to age 24. No problem is too big or too small for YouthLine.",
  },
];

const topResources = resources.slice(0, 2);
const bottomResources = resources.slice(2);

const CrisisResources = () => {
  return (
    <section id="crisis" className="crisis-section py-24 px-6 bg-secondary">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="crisis-heading text-3xl md:text-4xl font-heading font-medium text-foreground mb-4">
            Crisis Resources
          </h2>
          <div className="w-16 h-px bg-accent mx-auto mb-6" />
          <p className="font-body text-muted-foreground font-light leading-relaxed max-w-xl mx-auto">
            If you or someone you know is in immediate danger, please call 911. The following resources are available 24/7.
          </p>
        </motion.div>

        {/* Top row: 2 featured cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {topResources.map((resource, index) => (
            <motion.div
              key={resource.name}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="crisis-card bg-background rounded-md p-6 border border-border flex items-start gap-5"
            >
              <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center shrink-0">
                <resource.icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
              </div>
              <div className="text-left">
                <h3 className="crisis-resource-name font-heading font-medium text-foreground mb-1">{resource.name}</h3>
                <a href={resource.href} {...(resource.href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})} className="crisis-resource-detail font-body text-primary font-semibold text-sm mb-3 underline underline-offset-2 inline-block">{resource.detail}</a>
                <p className="crisis-resource-description font-body text-muted-foreground text-sm font-light">{resource.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom row: 3 standard cards */}
        <div className="crisis-list grid md:grid-cols-3 gap-6">
          {bottomResources.map((resource, index) => (
            <motion.div
              key={resource.name}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (index + 2) * 0.1 }}
              className="crisis-card bg-background rounded-md p-6 border border-border text-left"
            >
              <resource.icon className="w-6 h-6 text-primary mb-4" strokeWidth={1.5} />
              <h3 className="crisis-resource-name font-heading font-medium text-foreground mb-1">{resource.name}</h3>
              <a href={resource.href} {...(resource.href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})} className="crisis-resource-detail font-body text-primary font-semibold text-sm mb-3 underline underline-offset-2 inline-block">{resource.detail}</a>
              <p className="crisis-resource-description font-body text-muted-foreground text-sm font-light">{resource.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CrisisResources;
